/**
 * The scope picker's rows — "All regions" and then the tree (BON-534).
 *
 * Shared by the desktop dropdown and the mobile sheet, exactly as the design
 * shares them, so the two presentations cannot drift apart on what they list or
 * how the hierarchy reads. Only the METRICS differ, and those are chosen by
 * `variant`: a sheet row is a 44px tap target with a 44px caret beside it, a
 * menu row is the denser pointer-sized version.
 *
 * The branch holding the current scope opens on mount, so the picker shows you
 * where you already are rather than making you find it.
 *
 * Always alphabetical, at every level — the picker exists to find a region by
 * name, so any other order makes it a scan. Deliberately NOT tied to the
 * roster's sort control: re-ranking the roster should not move the names here
 * underneath the reader.
 */
import { useState } from 'react';

import { byName } from '../../lib/compare';

import { nestRows, sortForest } from './nest-rows';
import type { ScopeRowVariant } from './scope-row-metrics';
import type { ScopeHrefFor, ScopeRegion } from './scope.types';
import { ScopeAllRegionsRow } from './ScopeAllRegionsRow';
import { ScopeMenuRow } from './ScopeMenuRow';

export interface ScopeRowsProps {
  regions: ScopeRegion[];
  /** The region currently being viewed; null at all-regions scope. */
  currentRegionId: string | null;
  hrefFor: ScopeHrefFor;
  onPick: () => void;
  variant: ScopeRowVariant;
  /** False where the all-regions scope is not this viewer's to read — the
   *  national dashboards endpoints are Admin-only, and offering the row would
   *  offer a 403. */
  showAllRegions?: boolean;
  /** A "Your regions" row above All regions, for a viewer assigned several. */
  ownRegions?: OwnRegionsRow;
}

/** The "Your regions" row: where it goes, how many it stands for, and whether it is current. */
export interface OwnRegionsRow {
  href: string;
  count: number;
  isHere: boolean;
}

export function ScopeRows({
  regions,
  currentRegionId,
  hrefFor,
  onPick,
  variant,
  showAllRegions = true,
  ownRegions,
}: ScopeRowsProps) {
  const forest = sortForest(nestRows(regions), (a, b) => byName(a.row, b.row));
  const topLevelCount = forest.length;
  // Only one whole-scope row is current: Your regions when the union is showing.
  const isHere = currentRegionId === null && !ownRegions?.isHere;

  // Every ancestor of the current scope, so its branch is already open. Walked
  // through `parentId` rather than compared as paths — `seen` stops a parent
  // loop in pre-trigger data from hanging the render.
  const [openIds, setOpenIds] = useState<Set<string>>(() => {
    const byId = new Map(regions.map((r) => [r.id, r]));
    const open = new Set<string>();
    let parentId = currentRegionId ? (byId.get(currentRegionId)?.parentId ?? null) : null;
    while (parentId && !open.has(parentId)) {
      open.add(parentId);
      parentId = byId.get(parentId)?.parentId ?? null;
    }
    return open;
  });

  const toggle = (regionId: string) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(regionId)) next.delete(regionId);
      else next.add(regionId);
      return next;
    });

  return (
    <>
      {ownRegions && (
        <ScopeAllRegionsRow
          label="Your regions"
          to={ownRegions.href}
          topLevelCount={ownRegions.count}
          isHere={ownRegions.isHere}
          hrefFor={hrefFor}
          onPick={onPick}
          variant={variant}
        />
      )}
      {showAllRegions && (
        <ScopeAllRegionsRow
          topLevelCount={topLevelCount}
          isHere={isHere}
          hrefFor={hrefFor}
          onPick={onPick}
          variant={variant}
        />
      )}

      {(showAllRegions || ownRegions) && (
        <div style={{ height: 1, background: 'var(--nb-border)', margin: '0.3rem 0.5rem' }} />
      )}

      {forest.map((node) => (
        <ScopeMenuRow
          key={node.row.id}
          node={node}
          currentRegionId={currentRegionId}
          onToggle={toggle}
          hrefFor={hrefFor}
          onPick={onPick}
          openIds={openIds}
          variant={variant}
        />
      ))}
    </>
  );
}
